/**
 * Footer strip for the Ruta AI panel.
 *
 * While AI overage is off, this is one sentence — `PLATFORM_AI_INCLUDED_COPY`
 * — not a used/included tank. The quota gauge returns when Product sells AI.
 */
import { motion, useReducedMotion } from 'framer-motion';
import {
  PLATFORM_AI_INCLUDED_COPY,
  PLATFORM_AI_OVERAGE_ENABLED,
  type UsageMeter,
} from '@ruta/shared';
import { cn } from '@/lib/utils';
import { ASSISTANT_BRAND } from '@/components/ai-elements/assistant-brand';
import { AnimatedCount } from '@/components/dashboard/live-ops/AnimatedCount';

export type AiCreditsTone = 'healthy' | 'low' | 'over';

export function aiCreditsTone(meter: Pick<UsageMeter, 'used' | 'included' | 'overage'>): AiCreditsTone {
  if (meter.overage > 0 || (meter.included > 0 && meter.used >= meter.included)) return 'over';
  if (meter.included > 0 && meter.used / meter.included >= 0.8) return 'low';
  return 'healthy';
}

/** Share of the included allowance already burned (0–100). Caps at 100 when over. */
export function aiCreditsUsedPct(meter: Pick<UsageMeter, 'used' | 'included'>): number {
  if (meter.included <= 0) return meter.used > 0 ? 100 : 0;
  return Math.min(100, (meter.used / meter.included) * 100);
}

const TONE_FILL: Record<AiCreditsTone, string> = {
  healthy: ASSISTANT_BRAND.sage,
  low: 'rgb(251 191 36)', // amber-400 — matches warning language used elsewhere on dark glass
  over: 'rgb(251 113 133)', // rose-400
};

const TONE_GLOW: Record<AiCreditsTone, string> = {
  healthy: ASSISTANT_BRAND.glow(0.55),
  low: 'rgba(251,191,36,0.55)',
  over: 'rgba(251,113,133,0.5)',
};

interface AiCreditsMeterProps {
  meter?: UsageMeter | null;
  /** Pulse the filament while a reply is streaming. */
  isGenerating?: boolean;
  className?: string;
}

/**
 * Footer strip for the Ruta AI panel: rolling used / available counts, then a
 * flush progress filament that lines the bottom edge of the modal.
 */
export function AiCreditsMeter({ meter, isGenerating = false, className }: AiCreditsMeterProps) {
  const reduce = useReducedMotion();

  if (!PLATFORM_AI_OVERAGE_ENABLED) {
    return (
      <div className={cn('shrink-0', className)}>
        <p className="px-3 py-1.5 text-[10px] leading-none text-white/55">
          {PLATFORM_AI_INCLUDED_COPY}
        </p>
        <div
          className="h-[3px] w-full"
          style={{
            backgroundColor: ASSISTANT_BRAND.sage,
            boxShadow: `0 0 6px ${ASSISTANT_BRAND.glow(0.55)}`,
          }}
        />
      </div>
    );
  }

  if (!meter) {
    return (
      <div className={cn('shrink-0', className)} aria-hidden>
        <div className="flex items-center justify-between gap-3 px-3 py-1.5">
          <div className="h-2.5 w-16 rounded bg-white/[0.06]" />
          <div className="h-2.5 w-20 rounded bg-white/[0.06]" />
        </div>
        <div className="h-[3px] w-full bg-white/[0.06]" />
      </div>
    );
  }

  const tone = aiCreditsTone(meter);
  const usedPct = aiCreditsUsedPct(meter);
  const remaining = Math.max(0, meter.included - meter.used);
  const fill = TONE_FILL[tone];
  const glow = TONE_GLOW[tone];

  const label =
    tone === 'over'
      ? `${meter.used.toLocaleString()} of ${meter.included.toLocaleString()} credits used, ${meter.overage.toLocaleString()} over plan`
      : `${meter.used.toLocaleString()} of ${meter.included.toLocaleString()} credits used, ${remaining.toLocaleString()} left`;

  return (
    <div
      className={cn('shrink-0', className)}
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={meter.included}
      aria-valuenow={Math.min(meter.used, meter.included)}
    >
      <div className="flex items-baseline justify-between gap-3 px-3 py-1.5 tabular-nums">
        <span className="text-[10px] leading-none text-white/45">
          <span className="font-semibold text-white/80">
            <AnimatedCount value={meter.used} duration={0.5} />
          </span>
          <span className="text-white/35"> / </span>
          <span className="text-white/55">{meter.included.toLocaleString()}</span>
          <span className="ml-1 text-white/35">credits used</span>
        </span>
        <span
          className={cn(
            'text-[10px] leading-none font-medium',
            tone === 'over' && 'text-rose-300/90',
            tone === 'low' && 'text-amber-300/90',
            tone === 'healthy' && 'text-white/55',
          )}
        >
          {tone === 'over' ? (
            <>
              <AnimatedCount value={meter.overage} duration={0.5} /> credits over
            </>
          ) : (
            <>
              <span className="text-white/80">
                <AnimatedCount value={remaining} duration={0.5} />
              </span>{' '}
              credits left
            </>
          )}
        </span>
      </div>

      {/* Full-bleed filament — lines the bottom edge of the rounded panel.
          Width animates; glow is a static box-shadow (no animated filter/shadow
          that would dirty paint and read as panel motion). */}
      <div className="relative h-[3px] w-full overflow-hidden bg-white/[0.08]">
        <motion.div
          className="absolute inset-y-0 left-0"
          style={{ backgroundColor: fill, boxShadow: `0 0 6px ${glow}` }}
          initial={false}
          animate={{ width: `${usedPct}%` }}
          transition={
            reduce
              ? { duration: 0 }
              : { duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }
          }
        />
        {isGenerating && usedPct > 0 && !reduce && (
          <GeneratingEmber pct={usedPct} color={fill} glow={glow} />
        )}
      </div>
    </div>
  );
}

function GeneratingEmber({
  pct,
  color,
  glow,
}: {
  pct: number;
  color: string;
  glow: string;
}) {
  // Rides the filament tip as usage updates while the model is streaming.
  return (
    <motion.div
      className="pointer-events-none absolute top-1/2 h-2 w-2 -translate-y-1/2 rounded-full"
      style={{
        left: `calc(${pct}% - 4px)`,
        backgroundColor: color,
        boxShadow: `0 0 12px ${glow}, 0 0 4px ${color}`,
      }}
      initial={{ opacity: 0.35, scale: 0.7 }}
      animate={{ opacity: [0.35, 1, 0.35], scale: [0.7, 1.15, 0.7] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
      aria-hidden
    />
  );
}
